import api from './api';

export interface AuthoringAssistantConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type AuthoringAssistantDraftTarget = 'course' | 'lesson' | 'material' | 'exercise';

export interface AuthoringAssistantDraftCourse {
  name?: string;
  description?: string;
  subject?: string;
  subjectSpecification?: string;
  startDate?: string;
  endDate?: string;
}

export interface AuthoringAssistantDraftLesson {
  title?: string;
  description?: string;
  order?: number;
  isActive?: boolean;
}

export interface AuthoringAssistantDraftMaterial {
  title?: string;
  description?: string;
  content?: string;
}

export interface AuthoringAssistantDraftExercise {
  type?: string | null;
  instructions?: string;
  additionalInfo?: string;
  config?: unknown;
}

export interface AuthoringAssistantQuickAction {
  id: string;
  label: string;
  prompt: string;
}

export interface AuthoringAssistantChatRequest {
  message: string;
  conversationHistory?: AuthoringAssistantConversationMessage[];
  courseId?: number;
  lessonId?: number;
  materialId?: number;
  exerciseId?: number;
  draftCourse?: AuthoringAssistantDraftCourse;
  draftLesson?: AuthoringAssistantDraftLesson;
  draftMaterial?: AuthoringAssistantDraftMaterial;
  draftExercise?: AuthoringAssistantDraftExercise;
}

export interface AuthoringAssistantChatResponse {
  reply: string;
  contextSummary: string;
  focusLabel: string;
  quickActions: AuthoringAssistantQuickAction[];
}

export interface AuthoringAssistantGeneratedCourseDraft {
  name: string;
  description: string;
  subject: string;
  subjectSpecification: string;
  startDate?: string;
  endDate?: string;
}

export interface AuthoringAssistantGeneratedLessonDraft {
  title: string;
  description: string;
}

export interface AuthoringAssistantGeneratedMaterialDraft {
  title: string;
  description: string;
  content: string;
}

export interface AuthoringAssistantGeneratedExerciseDraft {
  type?: string;
  instructions: string;
  additionalInfo: string;
  configPatch?: Record<string, unknown> | null;
}

export interface AuthoringAssistantGeneratedDraftResponse {
  summary: string;
  course?: AuthoringAssistantGeneratedCourseDraft;
  lesson?: AuthoringAssistantGeneratedLessonDraft;
  material?: AuthoringAssistantGeneratedMaterialDraft;
  exercise?: AuthoringAssistantGeneratedExerciseDraft;
}

export interface AuthoringAssistantGenerateDraftRequest {
  target: AuthoringAssistantDraftTarget;
  prompt?: string;
  mode?: 'create' | 'improve';
  courseId?: number;
  lessonId?: number;
  materialId?: number;
  exerciseId?: number;
  draftCourse?: AuthoringAssistantDraftCourse;
  draftLesson?: AuthoringAssistantDraftLesson;
  draftMaterial?: AuthoringAssistantDraftMaterial;
  draftExercise?: AuthoringAssistantDraftExercise;
}

export const chatWithAuthoringAssistant = async (
  payload: AuthoringAssistantChatRequest,
) => {
  const response = await api.post('/ai/authoring-assistant/chat', payload);
  return response.data.data as AuthoringAssistantChatResponse;
};

export const generateAuthoringAssistantDraft = async (
  payload: AuthoringAssistantGenerateDraftRequest,
) => {
  const response = await api.post('/ai/authoring-assistant/generate-draft', payload);
  return response.data.data as AuthoringAssistantGeneratedDraftResponse;
};